import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Database, Zap, MessageSquare, CalendarCheck, Loader2, Globe, Cpu, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { fadeInUpSmall } from "../../lib/animations";
import { track, EVENTS } from "../../lib/analytics";
import { getApiErrorMessage } from "../../lib/apiError";
import axios from "axios";
import { toast } from "sonner";
import Mascot from "../Mascot";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const features = [
  { icon: Database, label: "MLS Connected", color: "#3B82F6" },
  { icon: Zap, label: "Ads Running", color: "#39FF14" },
  { icon: MessageSquare, label: "Leads Captured", color: "#8B5CF6" },
  { icon: CalendarCheck, label: "Showings Booked", color: "#F97316" },
];

const scanSteps = [
  { label: "Scraping listing data...", icon: Globe },
  { label: "Analyzing property details...", icon: Cpu },
  { label: "Generating ad strategy...", icon: Sparkles },
];

export function HeroSection({ navigate, user }) {
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startScanAnimation = () => {
    setScanning(true);
    setScanStep(0);
    setScanProgress(0);

    let progress = 0;
    timerRef.current = setInterval(() => {
      progress += 2;
      setScanProgress(Math.min(progress, 100));

      if (progress >= 33 && progress < 34) setScanStep(1);
      if (progress >= 66 && progress < 67) setScanStep(2);

      if (progress >= 100) {
        clearInterval(timerRef.current);
        setTimeout(() => {
          setScanning(false);
          setScanProgress(0);
          setScanStep(0);
          localStorage.setItem('pendingListingUrl', scrapeUrl.trim());
          navigate('/pricing');
        }, 400);
      }
    }, 50);
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    const url = scrapeUrl.trim();
    track(EVENTS.SCRAPE_CLICKED, { url, has_url: !!url, logged_in: !!user });

    if (!url) {
      navigate('/pricing');
      return;
    }

    track(EVENTS.LISTING_URL_ENTERED, { url });
    if (user) {
      setAnalyzing(true);
      track(EVENTS.SCRAPE_STARTED, { url, logged_in: true });
      try {
        const response = await axios.post(`${API_URL}/properties/scrape`, { url });
        const propertyId = response.data?.property?.id;
        if (propertyId) {
          track(EVENTS.SCRAPE_COMPLETED, { url, property_id: propertyId });
          navigate('/analyzing', { state: { propertyId } });
        } else {
          track(EVENTS.SCRAPE_FAILED, { url, reason: "no_property_id" });
          toast.error('Could not analyze this listing');
          setAnalyzing(false);
        }
      } catch (error) {
        track(EVENTS.SCRAPE_FAILED, { url, error: error.response?.status || "network" });
        toast.error(getApiErrorMessage(error, 'Failed to analyze listing'));
        setAnalyzing(false);
      }
    } else {
      track(EVENTS.SCRAPE_STARTED, { url, logged_in: false });
      startScanAnimation();
    }
  };

  const isDisabled = analyzing || scanning;

  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-16 bg-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #39FF14 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="max-w-4xl mx-auto text-center relative">
        <motion.div
          {...fadeInUpSmall}
          transition={{ duration: 0.6 }}
        >
          {/* Mascot floating above headline */}
          <div className="flex justify-center mb-6">
            <Mascot variant="purple-kawaii" size={88} animation="float" priority alt="AdWup mascot waving hello" />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.1] mb-6">
            Turn any listing into{" "}
            <span className="bg-gradient-to-r from-[#39FF14] via-[#00D4FF] to-[#8B5CF6] bg-clip-text text-transparent">AI-generated ads, follow-up, and campaign execution</span>{" "}
            <span className="text-gray-900">in minutes</span>
          </h1>

          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
            Generate AI ads, launch campaigns, and manage your marketing — all from one platform built for real estate agents.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-[#39FF14] text-black hover:bg-[#32E012] font-semibold px-8 py-6 text-base h-auto shadow-md"
              data-testid="hero-cta-btn"
            >
              Get Founding Member Access <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById('video-demo')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-gray-300 text-gray-700 hover:border-gray-400 font-medium px-8 py-6 text-base h-auto"
              data-testid="hero-watch-demo-btn"
            >
              Watch Demo
            </Button>
          </div>
        </motion.div>

        {/* Feature Icons + Scrape Bar Card */}
        <motion.div
          {...fadeInUpSmall}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-3xl mx-auto"
          data-testid="hero-features-card"
        >
          {/* 4 Feature Icons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
            {features.map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <span className="text-sm font-medium text-gray-700">{f.label}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 mb-8" />

          {/* Scrape Bar + Scan Animation */}
          <AnimatePresence mode="wait">
            {scanning ? (
              <motion.div
                key="scan"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="py-2"
                data-testid="hero-scan-animation"
              >
                {/* Progress bar */}
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-5">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-[#39FF14] via-[#00D4FF] to-[#8B5CF6]"
                    style={{ width: `${scanProgress}%` }}
                    transition={{ duration: 0.05 }}
                  />
                </div>

                {/* Steps */}
                <div className="space-y-3">
                  {scanSteps.map((step, i) => {
                    const StepIcon = step.icon;
                    const isActive = i === scanStep;
                    const isDone = i < scanStep || scanProgress >= 100;

                    return (
                      <div
                        key={step.label}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 ${
                          isDone ? 'bg-[#39FF14]/10' : isActive ? 'bg-gray-50' : 'bg-transparent'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                          isDone ? 'bg-[#39FF14] text-black' : isActive ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-300'
                        }`}>
                          {isDone ? <CheckCircle className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                        </div>
                        <span className={`text-sm font-medium transition-colors duration-300 ${
                          isDone ? 'text-gray-900' : isActive ? 'text-gray-700' : 'text-gray-400'
                        }`}>
                          {step.label}
                        </span>
                        {isActive && !isDone && (
                          <Loader2 className="w-4 h-4 text-[#39FF14] animate-spin ml-auto" />
                        )}
                        {isDone && (
                          <span className="text-xs text-[#39FF14] font-semibold ml-auto">Done</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <p className="text-gray-500 text-sm mb-4">Try it now — paste any listing URL</p>
                <form onSubmit={handleAnalyze} className="flex flex-col sm:flex-row gap-3" data-testid="hero-scrape-form">
                  <input
                    type="text"
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="Paste Zillow, Realtor.com, or Redfin URL..."
                    className="flex-1 px-4 py-3.5 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] text-sm"
                    disabled={isDisabled}
                    data-testid="hero-scrape-input"
                  />
                  <Button
                    type="submit"
                    disabled={isDisabled}
                    className="bg-[#39FF14] text-black hover:bg-[#32E012] font-semibold px-6 py-3.5 h-auto text-sm whitespace-nowrap"
                    data-testid="hero-scrape-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyzing...
                      </>
                    ) : (
                      <>
                        Analyze Listing <ArrowRight className="w-4 h-4 ml-1.5" />
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
